import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CalendarDays, ChevronRight, Loader2, MapPin, Plus, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Input } from "../ui/input";
import { AdminGate } from "./AdminGate";
import { SyncBanner } from "./SyncBanner";
import { OutlookCard } from "./OutlookCard";
import { useOfflineSync } from "../../hooks/useOfflineSync";

const EVENTS_CACHE = "fff_events_cache";

const NewEventForm = ({ onCreated, onCancel }) => {
  const [f, setF] = useState({ name: "", event_date: new Date().toISOString().slice(0, 10), location: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/events", { ...f, location: f.location || null, event_date: f.event_date || null });
      onCreated(data.event);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't create event.");
    } finally {
      setBusy(false);
    }
  };
  const cls = "h-12 rounded-xl bg-white border-black/10 text-[16px]";
  return (
    <form onSubmit={submit} data-testid="new-event-form" className="rounded-3xl bg-white border border-black/10 p-4 space-y-3">
      <Input required placeholder="Event name, e.g. NYC Nonprofit Expo" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-event-name" className={cls} />
      <div className="grid grid-cols-2 gap-2">
        <Input type="date" value={f.event_date} onChange={(e) => setF({ ...f, event_date: e.target.value })} data-testid="new-event-date" className={cls} />
        <Input placeholder="Location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="new-event-location" className={cls} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={onCancel} className="h-12 rounded-full border border-black/15 font-bold text-[12px] uppercase tracking-[0.1em]">Cancel</button>
        <button type="submit" disabled={busy} data-testid="new-event-submit" className="h-12 rounded-full bg-brand-charcoal text-white font-bold text-[12px] uppercase tracking-[0.1em] disabled:opacity-60">
          {busy ? "Creating…" : "Create event"}
        </button>
      </div>
    </form>
  );
};

const EventRow = ({ ev }) => (
  <Link to={`/admin/events/${ev.id}`} data-testid={`event-row-${ev.id}`} className="flex items-center gap-3 rounded-2xl bg-white border border-black/10 p-4 active:scale-[0.99] transition-transform">
    <div className="min-w-0 flex-1">
      <p className="font-display font-black text-lg text-brand-charcoal truncate">{ev.name}</p>
      <p className="mt-0.5 flex flex-wrap gap-x-3 text-[12px] text-brand-charcoal/60">
        {ev.event_date && <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" />{ev.event_date}</span>}
        {ev.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{ev.location}</span>}
      </p>
    </div>
    <span className="inline-flex items-center gap-1 rounded-full bg-brand-teal/10 text-brand-teal px-3 py-1 text-[12px] font-bold" data-testid={`event-row-count-${ev.id}`}>
      <Users className="h-3.5 w-3.5" />{ev.contact_count}
    </span>
    <ChevronRight className="h-4 w-4 text-brand-charcoal/40" />
  </Link>
);

function EventsList() {
  const navigate = useNavigate();
  const [events, setEvents] = useState(null);
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    api.get("/admin/events")
      .then((r) => { setEvents(r.data.events); localStorage.setItem(EVENTS_CACHE, JSON.stringify(r.data.events)); })
      .catch(() => setEvents(JSON.parse(localStorage.getItem(EVENTS_CACHE) || "[]")));
  }, []);
  const { online, pending, syncing, syncNow } = useOfflineSync();
  return (
    <div className="min-h-screen bg-brand-cream/50" data-testid="events-page">
      <div className="max-w-lg mx-auto px-4 pt-5 pb-16">
        <Link to="/admin/orders" className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/60" data-testid="events-back-admin">
          <ArrowLeft className="h-3.5 w-3.5" /> Admin
        </Link>
        <h1 className="mt-3 font-display font-black text-4xl text-brand-charcoal">Event Capture</h1>
        <p className="mt-1 text-[14px] text-brand-charcoal/60">Scan cards, run a sign-up sheet or show a QR code at your table.</p>
        <SyncBanner online={online} pending={pending} syncing={syncing} onSync={syncNow} />
        {online && <OutlookCard />}
        <div className="mt-6">
          {creating ? (
            <NewEventForm onCancel={() => setCreating(false)} onCreated={(ev) => navigate(`/admin/events/${ev.id}`)} />
          ) : (
            <button type="button" onClick={() => setCreating(true)} disabled={!online} data-testid="events-new-button" className="w-full h-14 rounded-full bg-brand-charcoal text-white font-bold uppercase tracking-[0.12em] text-[13px] inline-flex items-center justify-center gap-2 disabled:opacity-50">
              <Plus className="h-4 w-4" /> {online ? "New event" : "New event (needs signal)"}
            </button>
          )}
        </div>
        {online && (
          <p className="mt-2 text-center text-[11px] text-brand-charcoal/50">
            Heading somewhere with poor signal? Open your event here once before you leave.
          </p>
        )}
        <div className="mt-6 space-y-2.5" data-testid="events-list">
          {events === null && <Loader2 className="h-5 w-5 animate-spin text-brand-teal mx-auto" />}
          {events?.length === 0 && <p className="text-center text-[13px] text-brand-charcoal/55 py-8" data-testid="events-empty">No events yet. Create one before you head out.</p>}
          {events?.map((ev) => <EventRow key={ev.id} ev={ev} />)}
        </div>
      </div>
    </div>
  );
}

export default function EventsPage() {
  return <AdminGate><EventsList /></AdminGate>;
}
